import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  Mail,
  ChevronRight,
  Save,
  Eye,
  Code,
  FileText,
  ToggleLeft,
  ToggleRight,
  Sun,
  Moon,
} from 'lucide-react'
import { AppShell } from '@/components/shell'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/use-toast'

interface EmailTemplate {
  id: string
  slug: string
  name: string
  description: string | null
  subject: string
  body_html: string
  body_text: string
  variables: string[]
  active: boolean
  created_at: string
  updated_at: string
}

function useEmailTemplates() {
  return useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('name')
      if (error) throw error
      return data as EmailTemplate[]
    },
  })
}

// ── List View ──────────────────────────────────────────────────────────

function TemplateList({
  templates,
}: {
  templates: EmailTemplate[]
}) {
  return (
    <div className="space-y-2">
      {templates.map((t) => (
        <Link
          key={t.id}
          to={`/admin/emails/${t.slug}`}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-700
                     bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors group"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Mail className="h-5 w-5 text-zinc-400 shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {t.name}
                </span>
                {!t.active && (
                  <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500
                                   dark:bg-zinc-800 dark:text-zinc-500">
                    Disabled
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                {t.description || t.slug}
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors shrink-0" />
        </Link>
      ))}

      {templates.length === 0 && (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-8">
          No email templates found.
        </p>
      )}
    </div>
  )
}

// ── Edit View ──────────────────────────────────────────────────────────

type PreviewTab = 'html' | 'preview' | 'text'
type PreviewMode = 'light' | 'dark'

/**
 * Force the email preview to render in light or dark mode.
 * - Light: strips the @media (prefers-color-scheme: dark) block entirely
 * - Dark: extracts the dark rules and injects them as non-media-query styles
 */
function forceColorScheme(html: string, mode: PreviewMode): string {
  // Find @media (prefers-color-scheme: dark) blocks using brace counting
  // so nested CSS rules (e.g. body { ... }) are handled correctly.
  const marker = /@media\s*\(\s*prefers-color-scheme:\s*dark\s*\)\s*\{/g
  let darkRules = ''
  let result = html

  // Collect all dark-mode media blocks with their positions
  const blocks: { start: number; end: number; inner: string }[] = []
  let m: RegExpExecArray | null
  while ((m = marker.exec(html)) !== null) {
    const outerStart = m.index
    const bodyStart = outerStart + m[0].length
    let depth = 1
    let i = bodyStart
    while (i < html.length && depth > 0) {
      if (html[i] === '{') depth++
      else if (html[i] === '}') depth--
      i++
    }
    blocks.push({
      start: outerStart,
      end: i,
      inner: html.slice(bodyStart, i - 1),
    })
  }

  if (mode === 'light') {
    // Strip dark mode blocks (reverse order to preserve indices)
    for (let b = blocks.length - 1; b >= 0; b--) {
      result = result.slice(0, blocks[b].start) + result.slice(blocks[b].end)
    }
    return result
  }

  // Dark: extract inner rules and apply unconditionally
  for (const block of blocks) {
    darkRules += block.inner
  }

  const injection = `<style>${darkRules}</style>`
  if (html.includes('</head>')) {
    return html.replace('</head>', `${injection}</head>`)
  }
  return html + injection
}

function TemplateEditor({
  template,
}: {
  template: EmailTemplate
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [subject, setSubject] = useState(template.subject)
  const [bodyHtml, setBodyHtml] = useState(template.body_html)
  const [bodyText, setBodyText] = useState(template.body_text)
  const [active, setActive] = useState(template.active)
  const [previewTab, setPreviewTab] = useState<PreviewTab>('preview')
  const [previewMode, setPreviewMode] = useState<PreviewMode>('light')

  const hasChanges =
    subject !== template.subject ||
    bodyHtml !== template.body_html ||
    bodyText !== template.body_text ||
    active !== template.active

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('update-email-template', {
        body: {
          id: template.id,
          subject,
          body_html: bodyHtml,
          body_text: bodyText,
          active,
        },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      toast({ title: 'Template saved', description: `"${template.name}" has been updated.` })
    },
    onError: (err: Error) => {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' })
    },
  })

  const tabClass = (tab: PreviewTab) =>
    `px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
      previewTab === tab
        ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
        : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
    }`

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/emails"
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700
                     dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={!hasChanges || saveMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
                     bg-emerald-600 text-white hover:bg-emerald-700
                     disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save
        </button>
      </div>

      {/* Template Info */}
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          {template.name}
        </h2>
        {template.description && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            {template.description}
          </p>
        )}
        <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1 font-mono">
          slug: {template.slug}
        </p>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-700
                       bg-white dark:bg-zinc-900">
        <div>
          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Active</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            When disabled, this email will not be sent.
          </p>
        </div>
        <button
          onClick={() => setActive(!active)}
          className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors"
        >
          {active ? (
            <ToggleRight className="h-7 w-7 text-emerald-500" />
          ) : (
            <ToggleLeft className="h-7 w-7 text-zinc-400" />
          )}
        </button>
      </div>

      {/* Variables Reference */}
      {template.variables.length > 0 && (
        <div className="px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-900">
          <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2">
            Available Variables
          </p>
          <div className="flex flex-wrap gap-1.5">
            {template.variables.map((v) => (
              <code
                key={v}
                className="text-xs px-2 py-0.5 rounded bg-zinc-100 text-zinc-700
                           dark:bg-zinc-800 dark:text-zinc-300 font-mono"
              >
                {`{{${v}}}`}
              </code>
            ))}
          </div>
        </div>
      )}

      {/* Subject */}
      <div>
        <label
          htmlFor="email-subject"
          className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
        >
          Subject Line
        </label>
        <input
          id="email-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                     focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        />
      </div>

      {/* Body Editor with Preview Tabs */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Email Body
          </span>
          <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
            <button onClick={() => setPreviewTab('preview')} className={tabClass('preview')}>
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                Preview
              </span>
            </button>
            <button onClick={() => setPreviewTab('html')} className={tabClass('html')}>
              <span className="flex items-center gap-1">
                <Code className="h-3 w-3" />
                HTML
              </span>
            </button>
            <button onClick={() => setPreviewTab('text')} className={tabClass('text')}>
              <span className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Text
              </span>
            </button>
          </div>
        </div>

        {previewTab === 'preview' && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
            <div className="flex items-center justify-end gap-1 px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
              <span className="text-[11px] text-zinc-400 mr-1">Preview as</span>
              <button
                onClick={() => setPreviewMode('light')}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                  previewMode === 'light'
                    ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                    : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                }`}
              >
                <Sun className="h-3 w-3" />
                Light
              </button>
              <button
                onClick={() => setPreviewMode('dark')}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                  previewMode === 'dark'
                    ? 'bg-zinc-900 text-white shadow-sm dark:bg-zinc-600'
                    : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                }`}
              >
                <Moon className="h-3 w-3" />
                Dark
              </button>
            </div>
            <div style={{ background: previewMode === 'dark' ? '#1c1917' : '#ffffff' }}>
              <iframe
                srcDoc={forceColorScheme(bodyHtml, previewMode)}
                title="Email Preview"
                className="w-full border-0"
                style={{ minHeight: '500px' }}
                sandbox=""
              />
            </div>
          </div>
        )}

        {previewTab === 'html' && (
          <textarea
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={24}
            className="w-full px-3 py-2 text-xs font-mono rounded-lg border border-zinc-200 dark:border-zinc-700
                       bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                       focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-y"
          />
        )}

        {previewTab === 'text' && (
          <textarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={24}
            className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-zinc-200 dark:border-zinc-700
                       bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                       focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-y"
          />
        )}
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────

export default function AdminEmails() {
  const { isSuperAdmin } = useAuth()
  const { slug } = useParams<{ slug: string }>()
  const { data: templates, isLoading } = useEmailTemplates()

  const selectedTemplate = slug && templates
    ? templates.find((t) => t.slug === slug) ?? null
    : null

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="max-w-lg mx-auto px-4 py-6">
          <p className="text-sm text-zinc-500">Access denied.</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {!slug && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Link
                    to="/admin"
                    className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Link>
                  <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                    Email Templates
                  </h1>
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 ml-6">
                  Manage the content and design of transactional emails.
                </p>
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </div>
            ) : (
              <TemplateList
                templates={templates || []}
              />
            )}
          </>
        )}

        {slug && isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        )}

        {slug && !isLoading && !selectedTemplate && (
          <div className="text-center py-12">
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Template "{slug}" not found.
            </p>
            <Link to="/admin/emails" className="text-sm text-emerald-600 hover:underline mt-2 inline-block">
              Back to templates
            </Link>
          </div>
        )}

        {selectedTemplate && (
          <TemplateEditor
            key={selectedTemplate.id}
            template={selectedTemplate}
          />
        )}
      </div>
    </AppShell>
  )
}
